import type { HazWasteManifest, SimpleSelectable } from '../../tri-shared/types/db.ts';
import type { FileCreatedFor } from '../../tri-shared/types/files.ts';
import type { EpaEManifestManifest } from '../../tri-shared/types/hazwaste-epa-emanifest.ts';

/** The receiving facility details from box 8 of the paper form. */
export interface PaperManifestReceiver {
  name: string;
  address: { address1: string; city: string; state: string; zip: string };
  contactPhone: string;
}

export type PaperManifestReceiverField = 'name' | 'address' | 'phone';

export const PAPER_MANIFEST_RECEIVER_INCOMPLETE_MESSAGE =
  'Enter the receiving facility name, complete site address, and phone number from box 8 of the paper manifest.';

/** The box 8 entries that are still blank. Every one is required to record a paper manifest. */
export function paperManifestReceiverMissing(receiver: PaperManifestReceiver): PaperManifestReceiverField[] {
  const { address } = receiver;
  const missing: PaperManifestReceiverField[] = [];
  if (!receiver.name.trim()) missing.push('name');
  if (!address.address1.trim() || !address.city.trim() || !address.state.trim() || !address.zip.trim())
    missing.push('address');
  if (!receiver.contactPhone.trim()) missing.push('phone');
  return missing;
}

export const PAPER_MANIFEST_LINES_INCOMPLETE_MESSAGE =
  'Enter a positive quantity and whole container count for each paper manifest line.';

/** Paper lines need the quantity and container count from the form; EPA has nothing to fill them in from. */
export function paperManifestLinesComplete<L extends { quantity: number | null; containerCount: number | null }>(
  lines: L[],
): lines is (L & { quantity: number; containerCount: number })[] {
  return lines.every(
    (line) =>
      line.quantity != null &&
      line.quantity > 0 &&
      line.containerCount != null &&
      Number.isInteger(line.containerCount) &&
      line.containerCount >= 1,
  );
}

/**
 * The name to record for an EPA site: EPA's own record when it matches the ID, otherwise a name used on a
 * prior manifest. Blank when only the ID itself is known, so a name is never just the ID repeated.
 */
export function epaPartyName(
  epaSiteID: string,
  lookup: { epaSiteId: string; name: string } | null,
  priorParties: { epaSiteID: string; name: string }[],
): string {
  const id = epaSiteID.trim().toUpperCase();
  if (!id) return '';
  const lookupName = lookup?.epaSiteId.trim().toUpperCase() === id ? lookup.name.trim() : '';
  const name =
    lookupName || priorParties.find((party) => party.epaSiteID.trim().toUpperCase() === id)?.name.trim() || '';
  return name.toUpperCase() === id ? '' : name;
}

/** Labels a manifest PDF by what it is evidence of, using the EPA stage saved when the copy was retrieved. */
export function manifestPaperCopyLabel(createdFor: FileCreatedFor): { title: string; detail: string } {
  if (createdFor.type !== 'manifestPaper') return { title: 'Manifest copy', detail: 'Copy type not recorded' };
  if (createdFor.source === 'epa' && createdFor.epaDocumentType === 'form') {
    return {
      title: 'EPA-generated manifest',
      detail:
        createdFor.epaStatus === null
          ? 'Stage at retrieval not recorded'
          : `EPA stage when retrieved: ${createdFor.epaStatus.replace(/([a-z])([A-Z])/g, '$1 $2')}`,
    };
  }
  switch (createdFor.documentKind) {
    case 'initial':
      return { title: 'Shipment copy', detail: 'Generator and initial transporter signed copy' };
    case 'return':
      return {
        title: 'Signed return copy',
        detail:
          createdFor.source === 'epa' ? 'Receiving facility signed copy from EPA' : 'Receiving facility signed copy',
      };
    case 'supporting':
      return { title: 'Supporting document', detail: 'Additional shipment documentation' };
  }
}

/**
 * Which PDF EPA can provide for a synced manifest: its generated Form 8700-22 for electronic submissions, or the
 * scanned signed copy for paper submissions. `null` when the manifest has never been found in EPA.
 */
export function manifestEpaCopyAvailability(manifest: {
  source: string;
  lastSyncedAt: string | null;
  rawData: unknown;
}): 'generatedForm' | 'signedCopy' | null {
  if (!manifest.lastSyncedAt) return null;
  const snapshot = manifestData(manifest.rawData);
  if (snapshot?.submissionType === 'Hybrid' || snapshot?.submissionType === 'FullElectronic') return 'generatedForm';
  if (
    manifest.source === 'paper' ||
    snapshot?.printedDocument?.name ||
    snapshot?.submissionType === 'Image' ||
    snapshot?.submissionType === 'DataImage5Copy'
  )
    return 'signedCopy';
  return null;
}

/** Local paper progress is independent of the most recently imported EPA status. */
export function manifestStatusLabel(
  manifest: Pick<SimpleSelectable<HazWasteManifest>, 'source' | 'status' | 'shippedDate' | 'signedReturnAvailable'>,
): string {
  if (manifest.source !== 'paper') return manifest.status ?? 'Unknown status';
  if (manifest.signedReturnAvailable) return 'Signed return obtained';
  return manifest.shippedDate ? 'In transit' : 'Prepared';
}

export function manifestHasSignedReturn(manifest: EpaEManifestManifest): boolean {
  return Boolean(
    manifest.designatedFacility?.paperSignatureInfo?.signatureDate ||
    manifest.designatedFacility?.electronicSignaturesInfo?.some((signature) => signature.signatureDate),
  );
}

export function manifestData(value: unknown): EpaEManifestManifest | null {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value as EpaEManifestManifest;
}

/** An absent or still-processing waste list is not evidence of deleted lines. */
export function manifestHasCompleteLines(manifest: EpaEManifestManifest): boolean {
  return (
    Boolean(manifest.wastes?.length) &&
    (!manifest.ppcStatus || manifest.ppcStatus === 'DataQaCompleted' || manifest.ppcStatus === 'IndustryCorrected')
  );
}

/** Only compare provided EPA values. Local entries stay authoritative for paper records. */
export function paperManifestDifferences(
  local: EpaEManifestManifest | null,
  epa: EpaEManifestManifest | null,
): string[] {
  if (!local || !epa) return [];
  const differences: string[] = [];
  const compare = (label: string, first: unknown, second: unknown) => {
    if (second != null && JSON.stringify(first) !== JSON.stringify(second)) differences.push(label);
  };
  compare('Shipment date', local.shippedDate?.slice(0, 10), epa.shippedDate?.slice(0, 10));
  compare('Receiving facility', local.designatedFacility?.epaSiteId, epa.designatedFacility?.epaSiteId);
  compare(
    'Transporters',
    local.transporters?.map((site) => site.epaSiteId),
    epa.transporters?.map((site) => site.epaSiteId),
  );
  if (manifestHasCompleteLines(epa)) {
    compare('Number of waste lines', local.wastes?.length, epa.wastes?.length);
    for (const line of epa.wastes ?? []) {
      const original = local.wastes?.find((item) => item.lineNumber === line.lineNumber);
      if (!original) continue;
      compare(`Line ${line.lineNumber}: description`, original.wasteDescription, line.wasteDescription);
      compare(`Line ${line.lineNumber}: quantity`, original.quantity?.quantity, line.quantity?.quantity);
      compare(
        `Line ${line.lineNumber}: quantity unit`,
        original.quantity?.unitOfMeasurement?.code,
        line.quantity?.unitOfMeasurement?.code,
      );
      compare(
        `Line ${line.lineNumber}: container count`,
        original.quantity?.containerNumber,
        line.quantity?.containerNumber,
      );
      compare(
        `Line ${line.lineNumber}: container type`,
        original.quantity?.containerType?.code,
        line.quantity?.containerType?.code,
      );
      compare(
        `Line ${line.lineNumber}: federal waste codes`,
        original.hazardousWaste?.federalWasteCodes?.map((c) => c.code).sort(),
        line.hazardousWaste?.federalWasteCodes?.map((c) => c.code).sort(),
      );
      compare(
        `Line ${line.lineNumber}: state waste codes`,
        original.hazardousWaste?.generatorStateWasteCodes?.map((c) => c.code).sort(),
        line.hazardousWaste?.generatorStateWasteCodes?.map((c) => c.code).sort(),
      );
    }
  }
  return differences;
}

export function manifestEpaLinkLabel(
  manifest: Pick<SimpleSelectable<HazWasteManifest>, 'lastSyncedAt' | 'lastSyncAttemptAt' | 'epaSyncError'>,
): string {
  if (manifest.epaSyncError)
    return /404|not found|not yet available/i.test(manifest.epaSyncError)
      ? 'Not yet available in EPA'
      : 'EPA check needs attention';
  if (manifest.lastSyncedAt) return 'EPA linked';
  return manifest.lastSyncAttemptAt ? 'Not yet available in EPA' : 'EPA not checked';
}
